/**
 * useGate — the reader half of a password gate.
 *
 * A gated view renders `<PasswordGate>` while locked and its real shell once unlocked. Both
 * halves used to name the storage key independently (the gate wrote `'g3_unlocked'`, the
 * sibling `index.tsx` read it back as a bare string literal), so nothing tied them together.
 * Taking the same `GateNamespace` the gate is handed removes that second copy of the key.
 */

import { useCallback, useState } from 'react';
import { type GateNamespace, isUnlocked } from '@/lib/infra/gateStorage';

/**
 * `unlocked` reflects storage as of first render — gates persist before calling back, so a
 * remount inside the same tab stays open. `unlock` is what a gate's `onUnlock` calls; it
 * only flips local state, since the gate has already written the unlock through.
 *
 * `expectedHash` is needed only by hash-bound namespaces; unbound ones ignore it.
 */
export function useGate(
  namespace: GateNamespace,
  expectedHash?: string,
): { unlocked: boolean; unlock: () => void } {
  const [unlocked, setUnlocked] = useState(() => isUnlocked(namespace, expectedHash));
  const unlock = useCallback(() => setUnlocked(true), []);
  return { unlocked, unlock };
}
